// Copyright 2017 Sorama B.V.

import { DOM, Disposable, BindingEngine } from "aurelia-framework";
import { IFilter } from "filters/IFilter";

interface IIdentifyable {
    readonly id: string;
}

class Row<T> {
    public checked: boolean;
    public item: T;

    constructor(item: T) {
        this.item = item;
    }

    public check(checked: boolean) {
        this.checked = checked;
    }

    public copyStateTo(otherRow: Row<T>): void {
        otherRow.checked = this.checked;
    }
}

class ParentRow<T, TChild extends IIdentifyable> extends Row<T> {
    public expanded: boolean;
    public childRows: Row<TChild>[];

    constructor(item: T, children?: TChild[]) {
        super(item);

        if (!children) children = [];

        this.childRows = children.map((x) => new Row(x));
    }

    public check(checked: boolean) {
        super.check(checked);
        this.childRows.forEach((x) => x.check(checked));
    }

    public copyStateTo(otherRow: ParentRow<T, TChild>): void {
        super.copyStateTo(otherRow);
        otherRow.expanded = this.expanded;

        this.childRows.forEach((childRow) => {
            const otherChildRow = childRow.item
                ? otherRow.childRows.find((x) => x.item.id === childRow.item.id)
                : undefined;

            if (otherChildRow) {
                childRow.copyStateTo(otherChildRow);
            }
        });
    }
}

export abstract class SelectableListBase<TItem extends IIdentifyable, TChildItem extends IIdentifyable> {
    //#region Constants
    public static ItemDeletedEventName = "item-deleted";
    public static AddItemEventName = "add-item";
    //#endregion

    //#region Injected Services
    protected bindingEngine: BindingEngine;
    //#endregion

    //#region Elements
    protected element: Element;
    protected checkAllCheckbox: HTMLInputElement;
    //#endregion

    protected checkedItemsSubscription: Disposable;
    protected loading = false;

    protected itemRows: ParentRow<TItem, TChildItem>[] = [];

    public selectedItemId: string;
    public selectedChildItemId: string;
    public checkedItemIds: string[] = [];
    public filter: IFilter<TItem>;

    constructor(element: Element, bindingEngine: BindingEngine) {
        this.element = element;

        this.bindingEngine = bindingEngine;
    }

    public bind(): void {
        this.refresh();
    }

    public unbind(): void {
        if (this.checkedItemsSubscription) {
            this.checkedItemsSubscription.dispose();
            this.checkedItemsSubscription = null;
        }
    }

    public refresh(): Promise<void> {
        this.loading = true;
        return this.getItems()
            .then((items) => {
                if (this.filter) {
                    return this.filter.filter(items);
                } else {
                    return items;
                }
            })
            .then((items) => {
                return Promise.all(
                    items.map((item) =>
                        this.getChildItemsFromItem(item).then((childItems) => {
                            const row = new ParentRow(item, childItems);

                            // Check if the row with the item was already present in the list, if so, preserve state
                            const existingRow = row.item
                                ? this.itemRows.find((x) => x.item.id === row.item.id)
                                : undefined;
                            if (existingRow) {
                                existingRow.copyStateTo(row);
                            }

                            return row;
                        })
                    )
                );
            })
            .then((rows) => {
                this.itemRows = rows;
            })
            .catch((e) => {
                console.error(e);
            })
            .then(() => {
                this.loading = false;
            });
    }

    public toggleRow(row: ParentRow<TItem, TChildItem>, childRow: Row<TChildItem>): void {
        if (!childRow) {
            // Deselect child id if any
            if (this.selectedChildItemId) {
                this.selectedChildItemId = null;
            }
        }

        // Select or deselect item
        if (!childRow && this.selectedItemId === row.item.id) {
            this.selectedChildItemId = null;
            this.selectedItemId = null;
        } else {
            this.selectedItemId = row.item.id;

            if (childRow) {
                // Select or deselect the child
                if (this.selectedChildItemId === childRow.item.id) {
                    this.selectedChildItemId = null;
                } else {
                    this.selectedChildItemId = childRow.item.id;
                }
            }
        }
    }

    public checkRow(row: ParentRow<TItem, TChildItem> | Row<TChildItem>, checked: boolean, propagate = true): void {
        row.check(checked);

        // Set the state of the main checkbox
        if (this.isEverythingChecked()) {
            this.checkAllCheckbox.checked = true;
            this.checkAllCheckbox.indeterminate = false;
        } else if (this.isNothingChecked()) {
            this.checkAllCheckbox.checked = false;
            this.checkAllCheckbox.indeterminate = false;
        } else {
            this.checkAllCheckbox.indeterminate = true;
        }

        if (propagate && this.checkedItemIds) {
            if (checked) {
                if (this.checkedItemIds.some((x) => x === row.item.id)) return;

                this.checkedItemIds.push(row.item.id);
            } else {
                const index = this.checkedItemIds.findIndex((x) => x === row.item.id);
                if (index === -1) return;

                this.checkedItemIds.splice(index, 1);
            }
        }
    }

    public deleteParentRow(row: ParentRow<TItem, TChildItem>): void {
        this.deleteItem(row.item.id).then(() => {
            this.onItemDeleted(row.item.id);
            this.refresh();
        });
    }

    public deleteRow(row: Row<TChildItem>, parentRow: ParentRow<TItem, TChildItem>): void {
        this.deleteChildItem(row.item.id, parentRow.item.id).then(() => this.refresh());
    }

    public deleteCheckedRows(): void {
        Promise.all(
            this.itemRows.map((row) => this.deleteItem(row.item.id).then(() => this.onItemDeleted(row.item.id)))
        ).then(() => this.refresh());
    }

    public checkAllRows(checked: boolean): void {
        if (!this.isEverythingChecked() && !this.isNothingChecked()) checked = false;

        this.itemRows.forEach((x) => this.checkRow(x, checked, true));
    }

    private filterChanged(): void {
        this.refresh();
    }

    private checkedItemsIdsChanged(): void {
        if (this.checkedItemsSubscription) {
            this.checkedItemsSubscription.dispose();
            this.checkedItemsSubscription = null;
        }

        if (!this.checkedItemIds) this.checkedItemIds = [];

        this.checkedItemsSubscription = this.bindingEngine
            .collectionObserver(this.checkedItemIds)
            .subscribe(this.checkedItemsIdsCollectionChanged);

        this.checkedItemsIdsCollectionChanged();
    }

    private checkedItemsIdsCollectionChanged = (): void => {
        // Internally uncheck all items
        this.itemRows.forEach((x) => this.checkRow(x, false, false));

        // Internally check all checked items
        for (const id of this.checkedItemIds) {
            const row = this.itemRows.find((x) => x.item.id === id);

            if (!row) continue;

            this.checkRow(row, true, false);
        }
    };

    private isEverythingChecked(): boolean {
        return this.itemRows.every((x) => x.checked);
    }

    private isNothingChecked(): boolean {
        return this.itemRows.every((x) => !x.checked);
    }

    //#region Events
    private onItemDeleted(id: string): void {
        const event = DOM.createCustomEvent(SelectableListBase.ItemDeletedEventName, {
            bubbles: true,
            cancelable: true,
            detail: {
                id: id,
            },
        });

        if (this.element) this.element.dispatchEvent(event);
    }

    public addItem(): void {
        const event = DOM.createCustomEvent(SelectableListBase.AddItemEventName, <CustomEventInit>{
            bubbles: true,
            cancelable: true,
        });

        this.element.dispatchEvent(event);
    }
    //#endregion

    //#region Abstract Methods
    protected abstract getItems(): Promise<TItem[]>;
    protected abstract getChildItemsFromItem(item: TItem): Promise<TChildItem[]>;
    protected abstract deleteItem(itemId: string): Promise<void>;
    protected abstract deleteChildItem(childItemId: string, itemId: string): Promise<void>;
    //#endregion
}
